import { Component, EventEmitter, Input, Output } from '@angular/core';

import { QualityManagementService } from '../../../../services/quality-management.service';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { ConfirmationDialogService } from '../../../../services/confirmation-dialog.service';

import { CompanyDocumentAttachment } from '../../../../models/entities/company-document';

@Component({
  selector: 'company-document-revision-attachments',
  imports: [],
  templateUrl: './company-document-revision-attachments.component.html',
  styleUrl: './company-document-revision-attachments.component.scss'
})
export class CompanyDocumentRevisionAttachmentsComponent {

  @Input() companyDocumentRevisionId?: number | null;
  @Input() attachments: CompanyDocumentAttachment[] = [];
  @Input() readonlyAttachmentsPanel: boolean = false;
  @Input() label: string = 'Upload/Download File';
  @Input() canRemoveAttachment: boolean = false;

  @Output() attachmentUploaded = new EventEmitter<void>();
  @Output() attachmentRemoved = new EventEmitter<void>();
  @Output() pendingFilesChange = new EventEmitter<File[]>();

  pendingFiles: File[] = [];
  isUploading: boolean = false;
  isRemoving: boolean = false;

  constructor(
    private qualityManagementService: QualityManagementService,
    private alertNotificationService: AlertNotificationService,
    private confirmationDialogService: ConfirmationDialogService
  ) { }

  get attachment(): CompanyDocumentAttachment | null {
    return this.attachments?.[0] ?? null;
  }

  get pendingFile(): File | null {
    return this.pendingFiles?.[0] ?? null;
  }

  get hasContent(): boolean {
    return !!this.attachment || !!this.pendingFile;
  }

  get canAddFile(): boolean {
    return !this.readonlyAttachmentsPanel && !this.hasContent;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      this.addFile(input.files[0]);
      input.value = '';
    }
  }

  downloadFile(file: CompanyDocumentAttachment): void {
    if (!file?.url) {
      this.alertNotificationService.warning('File URL not available');
      return;
    }

    this.qualityManagementService.attachmentPublicUrl(file.url).subscribe({
      next: (publicUrl: string) => {
        if (!publicUrl) {
          this.alertNotificationService.error('Error downloading file');
          return;
        }
        window.open(publicUrl, '_blank');
      },
      error: (error: any) => {
        console.error('Error downloading the attachment:', error);
        this.alertNotificationService.error('Error downloading file');
      }
    });
  }

  confirmRemovePendingFile(): void {
    if (!this.pendingFile) {
      return;
    }

    this.confirmationDialogService.open(
      `Are you confident in your decision to remove ${this.pendingFile.name}?`,
      () => this.clearPendingFiles()
    );
  }

  confirmRemoveAttachment(): void {
    const attachment = this.attachment;
    if (!attachment || !this.canRemoveAttachment || !this.companyDocumentRevisionId) {
      return;
    }

    if (!attachment.attachmentId) {
      this.alertNotificationService.error('Attachment id not available.');
      return;
    }

    this.confirmationDialogService.open(
      `Are you confident in your decision to remove ${attachment.fileNameOriginal}?`,
      () => this.removeAttachment(Number(attachment.attachmentId))
    );
  }

  clearPendingFiles(): void {
    this.pendingFiles = [];
    this.pendingFilesChange.emit([]);
  }

  private removeAttachment(attachmentId: number): void {
    this.isRemoving = true;
    this.qualityManagementService
      .deleteCompanyDocumentRevisionAttachment(this.companyDocumentRevisionId!, attachmentId)
      .subscribe({
        next: () => {
          this.isRemoving = false;
          this.alertNotificationService.success('Attachment removed.');
          this.attachmentRemoved.emit();
        },
        error: (error: any) => {
          console.error('Error removing the attachment:', error);
          this.isRemoving = false;
          this.alertNotificationService.error(
            this.qualityManagementService.companyDocumentErrorMessage(error, 'Failed to remove the attachment.')
          );
        }
      });
  }

  private addFile(file: File): void {
    if (!this.companyDocumentRevisionId) {
      this.pendingFiles = [file];
      this.pendingFilesChange.emit(this.pendingFiles);
      return;
    }

    this.isUploading = true;
    this.qualityManagementService
      .uploadCompanyDocumentRevisionAttachment(this.companyDocumentRevisionId, file)
      .subscribe({
        next: () => {
          this.isUploading = false;
          this.alertNotificationService.success('Attachment uploaded.');
          this.attachmentUploaded.emit();
        },
        error: (error: any) => {
          console.error('Error uploading the attachment:', error);
          this.isUploading = false;
          this.alertNotificationService.error(
            this.qualityManagementService.companyDocumentErrorMessage(error, 'Failed to upload the attachment.')
          );
        }
      });
  }
}
